import { Clock, Bell, CheckCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Button, Card } from '@design-system';
import { useSelector } from 'react-redux';
import { ContactSupportButton } from '@/components/ContactSupportButton';
import { NON_WAG_NO_QUOTES_CTA, WAG_NO_QUOTES_CTA } from '@/shared/lib/constants';
import { quoteWaitlistApi } from '@client/api/waterlily';
import { selectFeatureFlags, selectSession } from '@client/model/selectors';
import { SessionType } from '@shared/SessionType';
import type { API } from '@client/api/types';

interface NoQuotesNYScreenProps {
  clientId: string;
  advisorId?: string;
  signupSource: 'shop_insurance' | 'guided_planning';
}

export function NoQuotesNYScreen({ clientId, advisorId, signupSource }: NoQuotesNYScreenProps) {
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const {
    mutate: signup,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: (data: API.QuoteWaitlistSignupRequest) => quoteWaitlistApi.signup(data),
  });

  const handleNotifyMe = () => {
    signup({
      clientId,
      advisorId,
      state: 'NY',
      reason: 'ny_ltc_quotes',
      signupSource,
    });
  };

  return (
    <div className="mx-auto max-w-xl py-12 text-center">
      <Card className="flex flex-col items-center gap-5 px-10 py-8">
        <div className="bg-primary-50 flex size-14 items-center justify-center rounded-full">
          <Clock className="text-primary size-7" />
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-950">
            No Quotes in Our System for New York Yet
          </h3>
          <p className="text-foreground text-base leading-relaxed">
            Our system doesn&apos;t have quotes for New York yet, but that doesn&apos;t mean
            coverage isn&apos;t available. Long-term care insurance options do exist in New York —
            we&apos;re in the process of adding them and will notify you as soon as they&apos;re
            ready.{' '}
            {' '}
            {isD2C ? WAG_NO_QUOTES_CTA : NON_WAG_NO_QUOTES_CTA}
          </p>
        </div>

        <div className="flex flex-col items-center gap-3">
          {isSuccess ? (
            <div className="flex items-center gap-2 text-sm font-medium text-green-600">
              <CheckCircle className="size-4" />
              You&apos;re on the list — we&apos;ll be in touch!
            </div>
          ) : (
            <Button
              onClick={handleNotifyMe}
              disabled={isPending}
            >
              <Bell className="size-4" />
              {isPending ? 'Signing up...' : 'Notify Me When Available'}
            </Button>
          )}

          <ContactSupportButton
            subject="New York LTC insurance availability"
            variant="outline"
          />
        </div>

      </Card>
    </div>
  );
}
